import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import EditExecutiveForm from './EditExecutiveForm';
import CreateEmailAddressModal from './CreateEmailAddressModal';
import EmailAddressList from './EmailAddressList';
import UnfiledItemsSection from './UnfiledItemsSection';
import UnfiledTimelineView from './UnfiledTimelineView';
import { listUnfilledComponents, listEmailAddresses } from '../../services/emailAddressService';
import { User, Edit, Trash2, Plus, Building, Mail, Phone, AtSign, Inbox, Lock, Package, Clock, CheckCircle, XCircle, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useParadigm } from '../../contexts/ParadigmContext';

const ExecutiveManagement = () => {
  const { user } = useAuth();
  const { labels } = useParadigm();
  const isAdmin = user?.is_organization_admin || user?.role === 'superadmin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingExecutive, setEditingExecutive] = useState(null);
  const [executiveToDelete, setExecutiveToDelete] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('executives');
  const [unfiledCount, setUnfiledCount] = useState(0);

  // Email lock state
  const [execEmailsLocked, setExecEmailsLocked] = useState(false);
  const [activePackageName, setActivePackageName] = useState('');

  // Executive approval settings
  const [approvalMode, setApprovalMode] = useState('auto_approve');
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const loadExecs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${apiBase}/executives`, { headers });
      setExecutives(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError('Failed to fetch executives');
    } finally {
      setLoading(false);
    }
  };

  // Fetch unfiled count for the tab badge
  const fetchUnfiledCount = useCallback(async () => {
    try {
      const result = await listUnfilledComponents();
      setUnfiledCount(result.unfilled_components?.length || 0);
    } catch (err) {
      console.error('Error fetching unfiled count:', err);
    }
  }, []);

  // Fetch email lock status
  const fetchEmailLockStatus = useCallback(async () => {
    try {
      const result = await listEmailAddresses();
      setExecEmailsLocked(result.exec_emails_locked_by_package || false);
      setActivePackageName(result.active_package_name || '');
    } catch (err) {
      console.error('Error fetching email lock status:', err);
    }
  }, []);

  // Fetch executive approval settings
  const fetchApprovalSettings = useCallback(async () => {
    if (!user?.organization_id) return;
    try {
      const res = await axios.get(`${apiBase}/organizations/${user.organization_id}/executive-settings`, { headers });
      setApprovalMode(res.data.executive_approval_mode || 'auto_approve');
    } catch (err) {
      // Default to auto_approve if endpoint not available
    }
  }, [user?.organization_id]);

  const saveApprovalMode = async (mode) => {
    setSavingSettings(true);
    try {
      await axios.put(`${apiBase}/organizations/${user.organization_id}/executive-settings`,
        { executive_approval_mode: mode }, { headers });
      setApprovalMode(mode);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleApproveExecutive = async (execId) => {
    setApprovingId(execId);
    try {
      await axios.post(`${apiBase}/executives/${execId}/approve`, {}, { headers });
      await loadExecs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve executive');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectExecutive = async (execId) => {
    try {
      await axios.post(`${apiBase}/executives/${execId}/reject`,
        { reason: rejectionReason }, { headers });
      setRejectingId(null);
      setRejectionReason('');
      await loadExecs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reject executive');
    }
  };

  useEffect(() => {
    loadExecs();
    fetchUnfiledCount();
    fetchEmailLockStatus();
    fetchApprovalSettings();
  }, []);

  // Guide deep link: arrive with the new-executive form already open, then
  // consume the action so closing or refreshing does not reopen it.
  useEffect(() => {
    if (searchParams.get('action') !== 'new-executive') return;

    setEditingExecutive(null);
    setShowEditForm(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('action');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Auto-expand executive from URL query param
  useEffect(() => {
    const execId = searchParams.get('id');
    if (execId && executives.length > 0) {
      setExpandedCard(execId);
      // Scroll to the executive card after a short delay
      setTimeout(() => {
        const element = document.getElementById(`executive-card-${execId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [searchParams, executives]);

  const handleAddExecutive = () => {
    setEditingExecutive(null);
    setShowEditForm(true);
  };

  const handleEditExecutive = (executive) => {
    setEditingExecutive(executive);
    setShowEditForm(true);
  };

  const handleFormClose = () => {
    setShowEditForm(false);
    setEditingExecutive(null);
  };

  const handleSaveExecutive = async (executiveId, formData) => {
    try {
      if (executiveId) {
        // Update existing executive
        await axios.put(`${apiBase}/executives/${executiveId}`, formData, { headers });
      } else {
        // Create new executive
        await axios.post(`${apiBase}/executives`, formData, { headers });
      }
      await loadExecs();
      setShowEditForm(false);
      setEditingExecutive(null);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save executive');
    }
  };

  const deleteExec = async (id) => {
    try {
      await axios.delete(`${apiBase}/executives/${id}`, { headers });
      await loadExecs();
    } catch (e) {
      setError('Failed to delete executive');
    }
  };

  const handleCreateEmail = (executive) => {
    setSelectedExecutive(executive);
    setShowEmailModal(true);
  };

  const handleEmailCreated = () => {
    // Refresh email list for the expanded card
    if (expandedCard) {
      loadExecs();
    }
  };

  const toggleCardExpanded = (execId) => {
    setExpandedCard(expandedCard === execId ? null : execId);
  };

  const handleItemAssigned = () => {
    // Refresh unfiled count when an item is assigned
    fetchUnfiledCount();
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">{labels.executiveManagement}</h2>
          <p className="text-slate-600 text-sm sm:text-base">Manage {labels.executive.toLowerCase()} profiles and travel information</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title={`${labels.executive} settings`}
            >
              <Settings className="w-5 h-5" />
            </button>
          )}
          {activeTab === 'executives' && (
            <button
              onClick={handleAddExecutive}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 sm:py-2 rounded-lg transition-colors touch-target"
            >
              <Plus className="w-4 h-4" />
              {labels.addExecutive}
            </button>
          )}
        </div>
      </div>

      {/* Pending Approvals Section (admin only) */}
      {isAdmin && executives.filter(e => e.approval_status === 'pending').length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4" />
            Pending {labels.executive} Approvals ({executives.filter(e => e.approval_status === 'pending').length})
          </h3>
          <div className="space-y-3">
            {executives.filter(e => e.approval_status === 'pending').map(exec => (
              <div key={exec.id} className="flex items-center justify-between bg-white rounded-md p-3 border border-amber-100">
                <div>
                  <p className="font-medium text-slate-800">
                    {exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`}
                  </p>
                  <p className="text-sm text-slate-500">
                    {exec.email} {exec.created_by_user_name ? `— Requested by ${exec.created_by_user_name}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {rejectingId === exec.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Reason (optional)"
                        className="px-2 py-1 border border-gray-300 rounded text-sm w-40"
                      />
                      <button
                        onClick={() => handleRejectExecutive(exec.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleApproveExecutive(exec.id)}
                        disabled={approvingId === exec.id}
                        className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        {approvingId === exec.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => setRejectingId(exec.id)}
                        className="flex items-center gap-1 px-3 py-1 bg-white text-red-600 border border-red-300 rounded text-sm hover:bg-red-50"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Org Settings (admin only) */}
      {isAdmin && showSettings && (
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
            <Settings className="w-4 h-4" />
            {labels.executive} Settings
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Require admin approval for new {labels.executives.toLowerCase()}</p>
              <p className="text-xs text-gray-500">When enabled, new {labels.executives.toLowerCase()} created by {labels.eas} must be approved by an org admin before becoming active.</p>
            </div>
            <button
              onClick={() => saveApprovalMode(approvalMode === 'require_admin_approval' ? 'auto_approve' : 'require_admin_approval')}
              disabled={savingSettings}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                approvalMode === 'require_admin_approval' ? 'bg-blue-600' : 'bg-gray-300'
              } ${savingSettings ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                approvalMode === 'require_admin_approval' ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="-mb-px flex gap-6">
          <button
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'executives'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-600 hover:text-slate-800 hover:border-slate-300'
            }`}
            onClick={() => setActiveTab('executives')}
          >
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {labels.executives}
            </span>
          </button>
          <button
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'unfiled'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-600 hover:text-slate-800 hover:border-slate-300'
            }`}
            onClick={() => setActiveTab('unfiled')}
          >
            <span className="flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Unfiled
              {unfiledCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-violet-100 text-violet-700">
                  {unfiledCount}
                </span>
              )}
            </span>
          </button>
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {/* Executive Email Locked by Package Notice */}
      {execEmailsLocked && activeTab === 'executives' && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-full">
                <Lock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 mb-1">
                {labels.executive} Email Addresses Not Available in Current Package
              </h4>
              <p className="text-sm text-amber-700 mb-2">
                Dedicated email addresses for {labels.executives.toLowerCase()} are not included in the assigned package:
              </p>
              <div className="flex items-center space-x-2 bg-amber-100 rounded-md px-3 py-2 inline-flex">
                <Package className="h-4 w-4 text-amber-700" />
                <span className="font-medium text-amber-800">{activePackageName || 'Unknown Package'}</span>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Contact your administrator to upgrade to a package that includes dedicated {labels.executive.toLowerCase()} emails.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Executives Tab Content */}
      {activeTab === 'executives' && (
        <>
          {loading && (
            <div className="text-center py-8">
              <div className="text-slate-500">Loading {labels.executives.toLowerCase()}...</div>
            </div>
          )}

          {/* Show Edit Form */}
          {showEditForm && (
            <div className="mb-6 bg-slate-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {editingExecutive ? `Edit ${labels.executiveProfile}` : `Add New ${labels.executive}`}
                </h3>
                <button
                  onClick={handleFormClose}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <EditExecutiveForm
                executive={editingExecutive}
                onSave={handleSaveExecutive}
                onCancel={handleFormClose}
              />
            </div>
          )}

          {/* Executive Cards Grid */}
          {!showEditForm && (
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {executives.map(executive => (
                <div key={executive.id} id={`executive-card-${executive.id}`} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center min-w-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800 truncate">
                            {executive.full_name || `${executive.first_name || ''} ${executive.last_name || ''}`}
                          </h3>
                          {executive.approval_status === 'pending' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" title="Awaiting admin approval">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </span>
                          )}
                          {executive.approval_status === 'rejected' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={executive.rejection_reason || 'Rejected'}>
                              <XCircle className="w-3 h-3 mr-1" />
                              Rejected
                            </span>
                          )}
                        </div>
                        {executive.company_title && (
                          <p className="text-sm text-slate-600 truncate">{executive.company_title}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    {executive.email && (
                      <div className="flex items-center text-sm text-slate-600">
                        <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{executive.email}</span>
                      </div>
                    )}
                    {executive.phone && (
                      <div className="flex items-center text-sm text-slate-600">
                        <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{executive.phone}</span>
                      </div>
                    )}
                    {executive.company && (
                      <div className="flex items-center text-sm text-slate-600">
                        <Building className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{executive.company}</span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:flex gap-2 sm:flex-wrap">
                    <button
                      onClick={() => handleEditExecutive(executive)}
                      className="flex items-center justify-center gap-1 text-sm bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-2 sm:py-1 rounded-lg sm:rounded touch-target"
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </button>
                    {!execEmailsLocked && (
                      <button
                        onClick={() => handleCreateEmail(executive)}
                        className="flex items-center justify-center gap-1 text-sm bg-violet-50 hover:bg-violet-100 text-violet-600 px-3 py-2 sm:py-1 rounded-lg sm:rounded touch-target"
                      >
                        <AtSign className="w-3 h-3" />
                        Email
                      </button>
                    )}
                    <button
                      onClick={() => toggleCardExpanded(executive.id)}
                      className="flex items-center justify-center gap-1 text-sm bg-slate-50 hover:bg-slate-100 text-slate-600 px-3 py-2 sm:py-1 rounded-lg sm:rounded touch-target"
                    >
                      {expandedCard === executive.id ? '▲ Hide' : '▼ More'}
                    </button>
                    <button
                      onClick={() => deleteExec(executive.id)}
                      className="flex items-center justify-center gap-1 text-sm bg-red-50 hover:bg-red-100 text-red-600 px-3 py-2 sm:py-1 rounded-lg sm:rounded touch-target"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>

                  {/* Expanded Section - Email Addresses & Unfiled Items */}
                  {expandedCard === executive.id && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-6">
                      {/* Email Addresses Section */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Addresses for Confirmations
                        </h4>
                        <EmailAddressList
                          executiveId={executive.id}
                          onEmailCreated={handleEmailCreated}
                        />
                      </div>

                      {/* Unfiled Items Section */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Inbox className="w-4 h-4" />
                          Unfiled Items
                        </h4>
                        <p className="text-xs text-slate-500 mb-3">
                          Items received via email that haven't been assigned to a trip yet
                        </p>
                        <UnfiledItemsSection
                          executiveId={executive.id}
                          onItemAssigned={handleItemAssigned}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {executives.length === 0 && !loading && (
                <div className="col-span-full text-center py-12">
                  <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-600 mb-2">{labels.noExecutives}</h3>
                  <p className="text-slate-500 mb-4">{labels.addAnExecutive}</p>
                  <button
                    onClick={handleAddExecutive}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                  >
                    {labels.addExecutive}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Unfiled Tab Content */}
      {activeTab === 'unfiled' && (
        <UnfiledTimelineView onItemAssigned={handleItemAssigned} />
      )}

      {/* Email Address Creation Modal */}
      {showEmailModal && selectedExecutive && (
        <CreateEmailAddressModal
          open={showEmailModal}
          onOpenChange={setShowEmailModal}
          type="executive"
          executiveId={selectedExecutive.id}
          executiveName={`${selectedExecutive.first_name} ${selectedExecutive.last_name}`}
          onSuccess={handleEmailCreated}
        />
      )}
    </div>
  );
};

export default ExecutiveManagement;
